import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ExternalLink, Plus } from "lucide-react";
import { fetchWithAuth } from "../../../../api-client/APIClient";
import EmptyStateWrapper from "../../../shared/EmptyStateWrapper";
import { Webhook } from "../../../../models/Entities";
import { showSuccessToast } from "../../../shared/Toasts";
import { SectionHeaderWrapper } from "../../../shared/SectionHeaderWrapper";
import {
  DOCS_WEBHOOKS_MERGE_TO_YOU_PATH,
  INITIAL_AND_ANY_SYNC_GUIDE,
  navigateToAddWebhookPage,
  navigateToEditIndividualWebhookPage,
} from "../../../../router/RouterUtils";
import {
  Alert,
  Button,
  Dialog,
  ButtonVariant,
  Link,
  Accordion,
  Card,
  Text,
  CopyTextField,
} from "@merge-api/merge-javascript-shared";
import { HookEvent } from "./ConfigurationAddEditWebhooksPage/enums";
import WebhookRow from "./ConfigurationAddEditWebhooksPage/components/WebhookRow";
import useAppContext from "../../../context/useAppContext";

const ConfigurationWebhookEmittersPage = () => {
  const { isUserPrivileged } = useAppContext();
  const MAX_SHOW_COLLAPSED_WEBHOOKS_COUNT = 5;
  const history = useHistory();
  const [webhooks, setWebhooks] = useState<Array<Webhook>>();
  const [webhookKey, setWebhookKey] = useState<string>("Loading...");
  const [isShowingAllWebhooks, setIsShowingAllWebhooks] = useState<boolean>(false);
  const [isSignatureBeingRegenerated, setIsSignatureBeingRegenerated] = useState<boolean>(false);
  const [isSignatureBeingRegeneratedLoading, setIsSignatureBeingRegeneratedLoading] =
    useState<boolean>(false);

  useEffect(() => {
    fetchWebhooks();
    fetchWebhookKey();
  }, []);

  const fetchWebhooks = () => {
    fetchWithAuth({
      path: "/integrations/webhooks/targets",
      method: "GET",
      onResponse: (data) => {
        setWebhooks(data);
      },
    });
  };

  const fetchWebhookKey = () => {
    fetchWithAuth({
      path: "/integrations/configuration/webhooks",
      method: "GET",
      onResponse: (data) => {
        setWebhookKey(data.webhook_key);
      },
    });
  };

  const regenerateWebhookKey = () => {
    setIsSignatureBeingRegeneratedLoading(true);
    fetchWithAuth({
      path: "/integrations/configuration/webhooks",
      method: "PATCH",
      onResponse: (data) => {
        setWebhookKey(data.webhook_key);
        setIsSignatureBeingRegenerated(false);
        setIsSignatureBeingRegeneratedLoading(false);
        showSuccessToast("Successfully regenerated webhook key!");
      },
    });
  };

  const subscribedWebhookEvents = new Set(webhooks?.map((webhook) => webhook.events).flat());
  const isUsingAnySyncWebhook = subscribedWebhookEvents.has(HookEvent.ACCOUNT_SYNCED_HOOK);
  const isUsingInitalSyncWebhook =
    subscribedWebhookEvents.has(HookEvent.ACCOUNT_SYNCED_INITIAL_HOOK) ||
    subscribedWebhookEvents.has(HookEvent.ACCOUNT_FULLY_SYNCED_INITIAL_HOOK);

  return (
    <>
      <Dialog
        open={isSignatureBeingRegenerated}
        onClose={() => setIsSignatureBeingRegenerated(false)}
        onPrimaryButtonClick={regenerateWebhookKey}
        primaryButtonLoading={isSignatureBeingRegeneratedLoading}
        primaryButtonText="Regenerate"
        onSecondaryButtonClick={() => setIsSignatureBeingRegenerated(false)}
        secondaryButtonText="Cancel"
        title="Regenerate signature?"
        variant="sm"
      >
        This will replace previous signatures and you will need to update any existing
        webhook-related request verification in your API.
      </Dialog>

      <SectionHeaderWrapper
        title="Merge webhooks"
        subtitle="Trigger POST requests to a specified URL when you want to be notified of a change in your integrations"
        docsUrl={DOCS_WEBHOOKS_MERGE_TO_YOU_PATH}
      >
        <>
          {(isUsingAnySyncWebhook || isUsingInitalSyncWebhook) && (
            <Alert color="yellow" showWarningIcon className="mb-6">
              <div>
                Our <b>initial sync</b> and <b>any sync</b> notification webhooks are deprecated.
                Creating new webhooks of these types is no longer supported. We highly recommend
                using our new <b>Linked Account synced</b> webhook.{" "}
                <a href={INITIAL_AND_ANY_SYNC_GUIDE} target="_blank" rel="noreferrer">
                  Learn more
                </a>
                .
              </div>
            </Alert>
          )}
        </>
        <Card className="w-full flex flex-col mb-8 min-w-0">
          <div className="flex flex-row px-6 py-5 items-center justify-between border-b border-gray-10 min-w-0">
            <Text variant="h4">Webhooks</Text>
            {isUserPrivileged && (
              <Button
                size="sm"
                variant={ButtonVariant.TertiaryWhite}
                leftIcon={<Plus size={12} />}
                onClick={() => navigateToAddWebhookPage(history)}
              >
                Webhook
              </Button>
            )}
          </div>
          {webhooks && webhooks.length === 0 ? (
            <EmptyStateWrapper title="No webhooks" />
          ) : !webhooks ? (
            Array(5)
              .fill(null)
              .map((_, index) => <WebhookRow isLoading key={index} isLastWebhook={index === 4} />)
          ) : (
            webhooks.slice(0, MAX_SHOW_COLLAPSED_WEBHOOKS_COUNT).map((webhook: Webhook, index) => {
              const isLastWebhook = webhooks.length < 5 && webhooks.length === index + 1;
              return (
                <WebhookRow
                  isLastWebhook={isLastWebhook}
                  isLoading={false}
                  webhook={webhook}
                  key={webhook.id}
                  onClick={() => navigateToEditIndividualWebhookPage(history, webhook.id)}
                />
              );
            })
          )}
          {webhooks && webhooks.length > 5 && (
            <Accordion
              hideChevron
              onChange={() => setIsShowingAllWebhooks(!isShowingAllWebhooks)}
              title={
                <Link className="w-full flex flex-row justify-center font-semibold text-blue-60 hover:text-blue-80">
                  {isShowingAllWebhooks ? (
                    <>View less</>
                  ) : (
                    <>View more ({webhooks.length - MAX_SHOW_COLLAPSED_WEBHOOKS_COUNT})</>
                  )}
                </Link>
              }
              titleClassName="px-6 py-4 hover:bg-gray-0 rounded-b-[10px]"
              variant="none"
              titlePosition="bottom"
            >
              {webhooks
                .slice(MAX_SHOW_COLLAPSED_WEBHOOKS_COUNT, webhooks.length)
                .map((webhook: Webhook, index) => {
                  const isLastWebhook = webhooks.length < 5 && webhooks.length === index + 1;
                  return (
                    <WebhookRow
                      isLastWebhook={isLastWebhook}
                      isLoading={false}
                      webhook={webhook}
                      key={webhook.id}
                      onClick={() => navigateToEditIndividualWebhookPage(history, webhook.id)}
                    />
                  );
                })}
            </Accordion>
          )}
        </Card>

        <Card className="w-full flex flex-col ">
          <div className="flex flex-row px-6 py-5 items-center justify-between border-b border-gray-10">
            <Text variant="h4">Security signature</Text>
            <Button
              rightIcon={<ExternalLink size={12} />}
              variant={ButtonVariant.TextBlue}
              size="sm"
              onClick={() => window.open(DOCS_WEBHOOKS_MERGE_TO_YOU_PATH, "_blank")}
            >
              Learn more in docs
            </Button>
          </div>
          <div className="flex flex-col px-6 py-5 text-left">
            <Text className="mb-4">
              <b>Important:</b> Your API should verify incoming POST requests with this signature
              for authenticity
            </Text>
            {/* Webhook signing key is a secret and is thus masked at the field (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot) */}
            <div className="ph-no-capture" data-fr-ignore>
              <CopyTextField
                text={webhookKey}
                onClickRegenerate={() => setIsSignatureBeingRegenerated(true)}
              />
            </div>
          </div>
        </Card>
      </SectionHeaderWrapper>
    </>
  );
};

export default ConfigurationWebhookEmittersPage;
